import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs/operators';
import { Utils } from '../../../core/Utils';
import { BcContestStatus, IBcAvailableContest, IBcContest, IBcContestEditModel } from '../../../models/BcContest';

import { AppService } from '../../../services/app.service';
import { BcContestService } from '../../../services/bc-contest.service';
import { bcState } from '../shared';

interface IAvailableContest extends IBcAvailableContest {
    id: string;
}

@Component({
    selector: 'app-bc-contest-edit',
    templateUrl: './bc-contest-edit.component.html',
    styleUrls: ['../bc-contest.scss']
})
export class BcContestEditComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: BcContestService,
        private route: ActivatedRoute
    ) { }

    readonly timePattern = '^([01]?[0-9]|2[0-3]):[0-5][0-9]$';

    readonly editorConfig = {
        convert_urls: false,
        forced_root_block: '',
        entity_encoding: 'raw',
        plugins: 'link textcolor table code lists',
        toolbar:
            'undo redo | formatselect | bold italic forecolor backcolor'
            + ' | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat'
            + ' | table link | code'
    };

    item: IBcContest;
    contestId: string;
    availableContests: IAvailableContest[] = [];
    availableStatuses: { text: string, value: BcContestStatus }[] = [];
    
    openedFromDate: Date;
    openedFromTime: string;
    openedToDate: Date;
    openedToTime: string;
    visibleFromDate: Date;
    visibleFromTime: string;
    visibleToDate: Date;
    visibleToTime: string;
    description: string;
    descriptionEN: string;
    status: BcContestStatus;
    courseLimit: number;

    isNew: boolean;
    isSubmitted: boolean;

    @ViewChild('form', { static: true }) private form: NgForm;

    
    ngOnInit() {
        const id = this.route.snapshot.params['id'];

        let statuses: BcContestStatus[] = [];

        if (!id) {
            this.isNew = true;
            this.status = 'Draft';
            this.openedFromTime = '00:00';
            this.openedToTime = '00:00';
            this.visibleFromTime = '00:00';
            this.visibleToTime = '00:00';

            statuses = ['Draft'];
        } else {
            statuses = ['Draft', 'Published', 'Cancelled'];

            this.loadById(+id);
        }

        this.availableStatuses = statuses.map(t => {
            return {
                text: this.app.translate(`bcContest_status${t}`),
                value: t
            }
        });

        this.app.addLoading(this.service.getAvailableContests()).subscribe(data => {
            this.availableContests = data.map(t => {
                return {
                    ...t,
                    id: this.buildContestId(t.PeriodId, t.Block)
                };
            });
        });
    }

    save() {
        this.isSubmitted = true;

        if (!this.form.valid) {
            this.app.alert.error(this.app.translate('bcContest_formError'));
            return false;
        }

        const contest = this.availableContests.find(t => t.id == this.contestId);

        const model: IBcContestEditModel = {
            Block: contest.Block,
            CourseLimit: this.courseLimit,
            Description: this.description,
            DescriptionEN: this.descriptionEN,
            OpenedFrom: `${Utils.getInputDateString(this.openedFromDate)} ${this.openedFromTime}`,
            OpenedTo: `${Utils.getInputDateString(this.openedToDate)} ${this.openedToTime}`,
            PeriodId: contest.PeriodId,
            Status: this.status,
            ResultsVisibleFrom: `${Utils.getInputDateString(this.visibleFromDate)} ${this.visibleFromTime}`,
            ResultsVisibleTo: `${Utils.getInputDateString(this.visibleToDate)} ${this.visibleToTime}`,
        };

        let id: number = this.item?.Id;

        const done = () => {
            this.app.navigateByUrl(`/bc-contests/${id}`);
        };

        const req = this.isNew
            ? this.service.create(model).pipe(tap(data => {
                id = data;
                bcState.requestSync(id);
            }))
            : this.service.update(id, model);

        this.app.addLoading(req).subscribe(() => {
            done();
        });
    }

    private loadById(id: number) {
        this.app.addLoading(this.service.getById(id)).subscribe(data => {
            this.item = data;

            this.contestId = this.buildContestId(data.PeriodId, data.Block);

            this.status = data.Status;
            this.courseLimit = data.CourseLimit;
            this.description = data.Description;
            this.descriptionEN = data.DescriptionEN;

            const openedFrom = Utils.parseDateTime(data.OpenedFrom);
            const openedTo = Utils.parseDateTime(data.OpenedTo);
            const visibleFrom = Utils.parseDateTime(data.ResultsVisibleFrom);
            const visibleTo = Utils.parseDateTime(data.ResultsVisibleTo);

            this.openedFromDate = new Date(openedFrom.year, openedFrom.month, openedFrom.date);
            this.openedFromTime = `${openedFrom.hoursString}:${openedFrom.minutesString}`;
            this.openedToDate = new Date(openedTo.year, openedTo.month, openedTo.date);
            this.openedToTime = `${openedTo.hoursString}:${openedTo.minutesString}`;

            this.visibleFromDate = new Date(visibleFrom.year, visibleFrom.month, visibleFrom.date);
            this.visibleFromTime = `${visibleFrom.hoursString}:${visibleFrom.minutesString}`;
            this.visibleToDate = new Date(visibleTo.year, visibleTo.month, visibleTo.date);
            this.visibleToTime = `${visibleTo.hoursString}:${visibleTo.minutesString}`;
        });
    }

    private buildContestId(planningPeriodId: string, block: string): string {
        return `${planningPeriodId}/${block}`;
    }
}
